import {
    AlertTriangle,
    Briefcase,
    ChevronDown,
    ChevronLeft,
    ChevronUp,
    CircleUserRound,
    Clock3,
    Landmark,
    LoaderCircle,
    Mail,
    Phone,
    Plus,
    ShieldCheck,
    Sparkles,
    Wallet,
    Zap,
} from 'lucide-react'
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { BASE_URL_API } from '../../References/URLs'
import ActiveStoreContext from '../../store/ActiveStoreContext'
import AuthContext from '../../store/auth-context'
import styles from './StaffCompensationProfileScreen.module.css'

const TYPE_META = {
    SERVICE_COMMISSION: { label: 'Service %', color: '#1565c0', bg: '#e3f2fd' },
    RETAIL_COMMISSION: { label: 'Retail %', color: '#00695c', bg: '#e0f2f1' },
    CATEGORY_COMMISSION: { label: 'By Category', color: '#6a1b9a', bg: '#f3e5f5' },
    SLAB_COMMISSION: { label: 'Tiered Slabs', color: '#e65100', bg: '#fff3e0' },
    TARGET_BONUS: { label: 'Target Bonus', color: '#2e7d32', bg: '#e8f5e9' },
    ATTENDANCE_BONUS: { label: 'Attendance Bonus', color: '#283593', bg: '#e8eaf6' },
    CUSTOM_INCENTIVE: { label: 'Custom', color: '#546e7a', bg: '#eceff1' },
}

const REVISION_TYPE_CONFIG = {
    JOINING: { label: 'Onboarded', color: '#2E7D32' },
    BASIC_AMOUNT_CHANGE: { label: 'Increment', color: '#1565C0' },
    SALARY_STRUCTURE_CHANGE: { label: 'Structure Changed', color: '#6A1B9A' },
    INCENTIVE_STRUCTURE_CHANGE: { label: 'Commission Changed', color: '#E65100' },
    INCENTIVE_RATE_CHANGE: { label: 'Rates Updated', color: '#00695C' },
    DESIGNATION_CHANGE: { label: 'Designation', color: '#827717' },
    EXIT: { label: 'Exit', color: '#C62828' },
}

const parseActiveStore = (rawStore) => {
    try {
        return rawStore && rawStore.trim() !== '' ? JSON.parse(rawStore) : null
    } catch {
        return null
    }
}

const formatDate = (value) => {
    if (!value) return '—'
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return value
    return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
}

const formatDateTime = (value) => {
    if (!value) return '—'
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return value
    return date.toLocaleString('en-IN', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    })
}

const formatAmount = (value) => `₹${Number(value || 0).toLocaleString('en-IN')}`

const RevisionHistoryCard = ({ revision }) => {
    const [isExpanded, setIsExpanded] = useState(false)

    const config = REVISION_TYPE_CONFIG[revision?.revisionType] || {
        label: revision?.revisionType || 'Updated',
        color: '#546E7A',
    }

    const before = revision?.previousValue || {}
    const after = revision?.newValue || {}

    return (
        <article className={styles.revisionCard}>
            <button type="button" className={styles.revisionHeadBtn} onClick={() => setIsExpanded((prev) => !prev)}>
                <div className={styles.revisionHeader}>
                    <span className={styles.revisionDate}>{formatDate(revision?.effectiveFrom)}</span>
                    <span className={styles.revisionChip} style={{ color: config.color, background: `${config.color}20` }}>
                        {config.label}
                    </span>
                </div>
                {isExpanded ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
            </button>

            <p className={styles.changeSummary}>{revision?.changeSummary || 'Compensation updated'}</p>
            {revision?.revisionReason && <p className={styles.revisionReason}>{revision.revisionReason}</p>}
            <p className={styles.revisedBy}>Revised by {revision?.revisedBy || '—'} · {formatDateTime(revision?.createdOn)}</p>

            {isExpanded && (
                <div className={styles.snapshotPanel}>
                    {Object.keys(before).length > 0 && (
                        <div className={styles.snapshotBlock}>
                            <p className={styles.snapshotLabel}>Before</p>
                            <p className={styles.snapshotText}>
                                Template: {before?.templateName || '—'}
                                {before?.basicAmount ? `\nBasic: ${formatAmount(before.basicAmount)}` : ''}
                                {before?.effectiveFrom ? `\nEffective: ${formatDate(before.effectiveFrom)}` : ''}
                            </p>
                        </div>
                    )}
                    <div className={styles.snapshotBlock}>
                        <p className={styles.snapshotLabel}>After</p>
                        <p className={styles.snapshotText}>
                            Template: {after?.templateName || '—'}
                            {after?.basicAmount ? `\nBasic: ${formatAmount(after.basicAmount)}` : ''}
                            {after?.effectiveFrom ? `\nEffective: ${formatDate(after.effectiveFrom)}` : ''}
                        </p>
                    </div>
                </div>
            )}
        </article>
    )
}

const StaffCompensationProfileScreen = () => {
    const navigate = useNavigate()
    const location = useLocation()
    const { staffIdentifier } = useParams()
    const authCtx = useContext(AuthContext)
    const asCtx = useContext(ActiveStoreContext)
    const activeStore = parseActiveStore(asCtx?.activeStore)
    const menuRef = useRef(null)

    const routeStaffName = location.state?.staffName || ''
    const storeIdentifier = activeStore?.storeIdentifier || ''

    const [profileData, setProfileData] = useState(null)
    const [isLoading, setIsLoading] = useState(true)
    const [isRefreshing, setIsRefreshing] = useState(false)
    const [activeTab, setActiveTab] = useState('SALARY')
    const [menuOpen, setMenuOpen] = useState(false)

    const fetchCompensationProfile = useCallback(async ({ silent = false } = {}) => {
        if (!authCtx?.token || !storeIdentifier || !staffIdentifier) {
            if (!silent) setIsLoading(false)
            return
        }

        if (!silent) setIsLoading(true)
        else setIsRefreshing(true)

        try {
            const response = await fetch(BASE_URL_API + 'getStaffCompensationProfile', {
                method: 'POST',
                body: JSON.stringify({
                    token: authCtx.token,
                    storeIdentifier,
                    staffIdentifier,
                }),
            })

            if (!response.ok) {
                toast.error('Failed to load compensation profile.')
                return
            }

            const respo = await response.json()
            if (respo?.status === 'success') {
                setProfileData(respo.response)
                return
            }

            if (respo?.message === 'tokenExpired') {
                authCtx.logout()
                return
            }

            if (respo?.errorCode === 'VAL_007') {
                toast.error('Staff member not found.')
                navigate(-1)
                return
            }

            toast.error(respo?.message || 'Failed to load profile.')
            navigate(-1)
        } catch (error) {
            console.error('fetchCompensationProfile error:', error)
            toast.error('Network error. Please check your connection.')
        } finally {
            setIsLoading(false)
            setIsRefreshing(false)
        }
    }, [authCtx, navigate, staffIdentifier, storeIdentifier])

    useEffect(() => {
        fetchCompensationProfile()
    }, [fetchCompensationProfile])

    useEffect(() => {
        if (!storeIdentifier || !staffIdentifier) return
        fetchCompensationProfile({ silent: true })
    }, [location.key]) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        const onFocus = () => {
            if (!storeIdentifier || !staffIdentifier) return
            fetchCompensationProfile({ silent: true })
        }
        const onVisibility = () => {
            if (document.visibilityState === 'visible') onFocus()
        }
        window.addEventListener('focus', onFocus)
        document.addEventListener('visibilitychange', onVisibility)
        return () => {
            window.removeEventListener('focus', onFocus)
            document.removeEventListener('visibilitychange', onVisibility)
        }
    }, [fetchCompensationProfile, staffIdentifier, storeIdentifier])

    useEffect(() => {
        const onOutside = (event) => {
            if (menuRef.current && !menuRef.current.contains(event.target)) setMenuOpen(false)
        }
        document.addEventListener('mousedown', onOutside)
        return () => document.removeEventListener('mousedown', onOutside)
    }, [])

    const staff = profileData?.staff || null
    const salaryAssignment = profileData?.salaryAssignment || null
    const incentiveAssignment = profileData?.incentiveAssignment || null
    const compensationStatus = profileData?.compensationStatus || {}
    const revisionHistory = Array.isArray(profileData?.revisionHistory) ? profileData.revisionHistory : []
    const totalRevisionCount = Number(profileData?.totalRevisionCount || revisionHistory.length || 0)

    const salaryComponents = useMemo(() => {
        const list = Array.isArray(salaryAssignment?.components) ? salaryAssignment.components : []
        return [...list].sort((a, b) => Number(a?.sortOrder || 0) - Number(b?.sortOrder || 0))
    }, [salaryAssignment])
    const salaryEarnings = salaryComponents.filter((component) => component.componentCategory === 'EARNING')
    const salaryDeductions = salaryComponents.filter((component) => component.componentCategory === 'DEDUCTION')

    const incentiveComponents = useMemo(() => {
        const list = Array.isArray(incentiveAssignment?.components) ? incentiveAssignment.components : []
        return [...list].sort((a, b) => Number(a?.sortOrder || 0) - Number(b?.sortOrder || 0))
    }, [incentiveAssignment])

    const profileName = staff?.staffName || routeStaffName || 'Compensation Profile'

    return (
        <div className={styles.page}>
            <header className={styles.header}>
                <button type="button" className={styles.backBtn} onClick={() => navigate(-1)}>
                    <ChevronLeft size={20} />
                </button>
                <div className={styles.headerMeta}>
                    <span className={styles.heroPill}><Sparkles size={12} /> Compensation</span>
                    <h1 className={styles.headerTitle}>{profileName}</h1>
                </div>
                <div className={styles.menuWrap} ref={menuRef}>
                    <button type="button" className={styles.menuBtn} onClick={() => setMenuOpen((prev) => !prev)}>
                        Assign / Revise <ChevronDown size={14} />
                    </button>
                    {menuOpen && (
                        <div className={styles.dropdownMenu}>
                            <button
                                type="button"
                                className={styles.dropdownItem}
                                onClick={() => {
                                    setMenuOpen(false)
                                    navigate('/payroll/assign-salary-structure', {
                                        state: { staffIdentifier, staffName: profileName, currentSalaryAssignment: salaryAssignment },
                                    })
                                }}
                            >
                                <Wallet size={14} /> Assign Salary Structure
                            </button>
                            {salaryAssignment && (
                                <button
                                    type="button"
                                    className={styles.dropdownItem}
                                    onClick={() => {
                                        setMenuOpen(false)
                                        navigate('/payroll/revise-salary', {
                                            state: {
                                                staffIdentifier,
                                                staffName: profileName,
                                                currentSalaryAssignment: salaryAssignment,
                                            },
                                        })
                                    }}
                                >
                                    <Sparkles size={14} /> Revise Salary
                                </button>
                            )}
                            <button
                                type="button"
                                className={styles.dropdownItem}
                                onClick={() => {
                                    setMenuOpen(false)
                                    navigate('/payroll/assign-incentive-plan', {
                                        state: { staffIdentifier, staffName: profileName, currentIncentiveAssignment: incentiveAssignment },
                                    })
                                }}
                            >
                                <Zap size={14} /> Assign Commission Plan
                            </button>
                        </div>
                    )}
                </div>
            </header>

            {isLoading && (
                <section className={styles.loaderCard}>
                    <LoaderCircle size={22} className={styles.spin} />
                    <p>Loading compensation profile…</p>
                </section>
            )}

            {!isLoading && !storeIdentifier && (
                <section className={styles.alertCard}>
                    <AlertTriangle size={15} />
                    <div>
                        <strong>Select an active store first.</strong>
                        <p>Compensation profile is store-specific.</p>
                    </div>
                </section>
            )}

            {!isLoading && storeIdentifier && profileData && (
                <>
                    <section className={styles.staffCard}>
                        <div className={styles.staffTopRow}>
                            <div className={styles.avatarWrap}>
                                {staff?.profileImg
                                    ? <img src={staff.profileImg} alt={profileName} className={styles.avatarImg} />
                                    : <CircleUserRound size={30} className={styles.avatarFallback} />}
                            </div>
                            <div className={styles.staffPrimary}>
                                <h2>{profileName}</h2>
                                <p>{staff?.designationLabel || 'Staff'}</p>
                                <div className={styles.contactRow}>
                                    {staff?.mobile && <span><Phone size={12} /> {staff.mobile}</span>}
                                    {staff?.email && <span><Mail size={12} /> {staff.email}</span>}
                                </div>
                            </div>
                        </div>
                        <div className={styles.badgeRow}>
                            {compensationStatus?.payrollReady
                                ? <span className={styles.readyBadge}><ShieldCheck size={12} /> Payroll Ready</span>
                                : <span className={styles.warnBadge}><AlertTriangle size={12} /> Salary Missing</span>}
                            <span className={styles.metaPill}><Clock3 size={12} /> Joined {formatDate(staff?.joiningDate)}</span>
                            {staff?.bankDetails?.bankName && (
                                <span className={styles.metaPill}><Landmark size={12} /> {staff.bankDetails.bankName}</span>
                            )}
                        </div>
                    </section>

                    <section className={styles.tabRow}>
                        <button type="button" className={`${styles.tabBtn} ${activeTab === 'SALARY' ? styles.tabBtnActive : ''}`} onClick={() => setActiveTab('SALARY')}>
                            <Wallet size={14} /> Salary
                        </button>
                        <button type="button" className={`${styles.tabBtn} ${activeTab === 'INCENTIVE' ? styles.tabBtnActive : ''}`} onClick={() => setActiveTab('INCENTIVE')}>
                            <Zap size={14} /> Commission
                        </button>
                        <button type="button" className={`${styles.tabBtn} ${activeTab === 'HISTORY' ? styles.tabBtnActive : ''}`} onClick={() => setActiveTab('HISTORY')}>
                            <Briefcase size={14} /> History ({totalRevisionCount})
                        </button>
                    </section>

                    {isRefreshing && <p className={styles.refreshHint}>Refreshing latest updates…</p>}

                    {activeTab === 'SALARY' && (
                        <section className={styles.tabContent}>
                            {!salaryAssignment ? (
                                <div className={styles.emptyState}>
                                    <div className={styles.emptyIllo}><Wallet size={28} /></div>
                                    <h3>No salary structure assigned.</h3>
                                    <button type="button" className={styles.emptyCtaBtn} onClick={() => navigate('/payroll/assign-salary-structure', { state: { staffIdentifier, staffName: profileName } })}>
                                        <Plus size={14} /> Assign Salary Structure
                                    </button>
                                </div>
                            ) : (
                                <article className={styles.assignmentCard}>
                                    <div className={styles.assignmentHead}>
                                        <h3>{salaryAssignment.templateName || 'Salary Structure'}</h3>
                                        <span className={styles.versionBadge}>v{salaryAssignment.templateVersion || 1}</span>
                                    </div>
                                    {salaryAssignment.designationTag && <p className={styles.designationTag}>{salaryAssignment.designationTag}</p>}
                                    <p className={styles.assignmentMeta}>Active from {formatDate(salaryAssignment.effectiveFrom)} · Basic: {formatAmount(salaryAssignment.basicAmount)}</p>
                                    {salaryAssignment.assignmentRemarks && <p className={styles.assignmentNote}>{salaryAssignment.assignmentRemarks}</p>}

                                    <div className={styles.componentSection}>
                                        {salaryEarnings.length > 0 && <p className={styles.groupLabel}>EARNINGS</p>}
                                        {salaryEarnings.map((component) => (
                                            <div key={component.componentIdentifier} className={styles.componentRow}>
                                                <span>{component.componentName}</span>
                                                <span>{component.valueDisplay || formatAmount(component.resolvedAmount)}</span>
                                            </div>
                                        ))}
                                        {salaryDeductions.length > 0 && <p className={styles.groupLabel}>DEDUCTIONS</p>}
                                        {salaryDeductions.map((component) => (
                                            <div key={component.componentIdentifier} className={styles.componentRow}>
                                                <span>{component.componentName}</span>
                                                <span>{component.valueDisplay || formatAmount(component.resolvedAmount)}</span>
                                            </div>
                                        ))}
                                    </div>

                                    <p className={styles.summaryLine}>
                                        Gross Earnings: {formatAmount(salaryAssignment?.summary?.totalEarnings)} ·
                                        Est. Net: {formatAmount(salaryAssignment?.summary?.estimatedNet)}
                                        {salaryAssignment?.summary?.isEstimate ? '*' : ''}
                                    </p>
                                    {salaryAssignment?.summary?.note && <p className={styles.summaryNote}>{salaryAssignment.summary.note}</p>}
                                </article>
                            )}
                        </section>
                    )}

                    {activeTab === 'INCENTIVE' && (
                        <section className={styles.tabContent}>
                            {!incentiveAssignment ? (
                                <div className={styles.emptyState}>
                                    <div className={styles.emptyIllo}><Zap size={28} /></div>
                                    <h3>No commission plan assigned.</h3>
                                    <button
                                        type="button"
                                        className={styles.emptyCtaBtn}
                                        onClick={() => navigate('/payroll/assign-incentive-plan', { state: { staffIdentifier, staffName: profileName, currentIncentiveAssignment: incentiveAssignment } })}
                                    >
                                        <Plus size={14} /> Assign Commission Plan
                                    </button>
                                </div>
                            ) : (
                                <article className={styles.assignmentCard}>
                                    <div className={styles.assignmentHead}>
                                        <h3>{incentiveAssignment.templateName || 'Commission Plan'}</h3>
                                        <span className={styles.versionBadge}>v{incentiveAssignment.templateVersion || 1}</span>
                                    </div>
                                    <p className={styles.assignmentMeta}>Active from {formatDate(incentiveAssignment.effectiveFrom)}</p>
                                    {incentiveAssignment.assignmentRemarks && <p className={styles.assignmentNote}>{incentiveAssignment.assignmentRemarks}</p>}

                                    <div className={styles.componentCardsWrap}>
                                        {incentiveComponents.map((component) => {
                                            const meta = TYPE_META[component.componentType] || { label: component.componentType, color: '#546e7a', bg: '#eceff1' }
                                            return (
                                                <div key={component.componentIdentifier} className={styles.incentiveComponentCard}>
                                                    <div className={styles.incentiveCardTop}>
                                                        <p className={styles.incentiveName}>{component.componentName}</p>
                                                        <span className={styles.typeChip} style={{ color: meta.color, background: meta.bg }}>{meta.label}</span>
                                                    </div>
                                                    <p className={styles.incentiveValue}>{component.valueDisplay || 'Configured rule'}</p>
                                                </div>
                                            )
                                        })}
                                    </div>
                                </article>
                            )}
                        </section>
                    )}

                    {activeTab === 'HISTORY' && (
                        <section className={styles.tabContent}>
                            {revisionHistory.length === 0 ? (
                                <div className={styles.emptyState}>
                                    <div className={styles.emptyIllo}><Briefcase size={28} /></div>
                                    <h3>No revision history yet.</h3>
                                </div>
                            ) : (
                                <div className={styles.timelineWrap}>
                                    {revisionHistory.map((revision) => (
                                        <RevisionHistoryCard key={revision.revisionIdentifier || `${revision.createdOn}-${revision.revisionType}`} revision={revision} />
                                    ))}
                                </div>
                            )}
                        </section>
                    )}
                </>
            )}
        </div>
    )
}

export default StaffCompensationProfileScreen